import { logger } from './loggerService';
import { getContrastColor } from '@/utils/colorUtils';

const CACHE_PREFIX = 'company_cache_';
const THEME_CACHE_KEY = 'company_theme_cache';
const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

class CompanyCache {
  /**
   * Cache company data with expiration
   * @param {string} companyId 
   * @param {object} companyData 
   */
  setCompany(companyId, companyData) {
    if (!companyId || !companyData) return;

    try {
      const cacheData = {
        ...companyData,
        _cached: Date.now(),
        _cacheVersion: '1.0'
      };

      localStorage.setItem(`${CACHE_PREFIX}${companyId}`, JSON.stringify(cacheData));
      
      logger.info('[CompanyCache] Company data cached', { 
        companyId, 
        companyName: companyData.name,
        hasLogo: !!companyData.logoUrl 
      });
    } catch (error) {
      logger.warn('[CompanyCache] Failed to cache company data', { 
        companyId, 
        error: error.message 
      });
    }
  }

  /**
   * Get cached company data if still valid
   * @param {string} companyId 
   * @returns {object|null} Company data or null if not cached/expired
   */
  getCompany(companyId) {
    if (!companyId) return null;

    try {
      const cachedData = localStorage.getItem(`${CACHE_PREFIX}${companyId}`);
      if (!cachedData) return null;

      const companyData = JSON.parse(cachedData);
      const cacheAge = Date.now() - (companyData._cached || 0);

      if (cacheAge > MAX_CACHE_AGE) {
        // Cache expired, remove it
        this.removeCompany(companyId);
        logger.info('[CompanyCache] Cache expired, removed', { 
          companyId, 
          cacheAge: Math.round(cacheAge / 1000 / 60 / 60) // hours
        });
        return null;
      }

      logger.info('[CompanyCache] Using cached company data', { 
        companyId, 
        cacheAge: Math.round(cacheAge / 1000 / 60), // minutes
        companyName: companyData.name 
      });

      // Remove cache metadata before returning
      const { _cached, _cacheVersion, ...cleanData } = companyData;
      return cleanData;
    } catch (error) {
      logger.warn('[CompanyCache] Failed to parse cached company data', { 
        companyId, 
        error: error.message 
      });
      this.removeCompany(companyId);
      return null;
    }
  }

  /**
   * Remove cached company data
   * @param {string} companyId 
   */
  removeCompany(companyId) {
    if (!companyId) return;
    
    try {
      localStorage.removeItem(`${CACHE_PREFIX}${companyId}`);
      logger.debug('[CompanyCache] Removed cached company data', { companyId });
    } catch (error) {
      logger.warn('[CompanyCache] Failed to remove cached company data', { 
        companyId, 
        error: error.message 
      });
    }
  }

  /**
   * Cache and apply theme immediately
   * @param {string} companyId 
   * @param {object} company 
   */
  setAndApplyTheme(companyId, company) {
    if (!companyId || !company) return;

    try {
      // Cache theme data (include brandTextColor so cached restores get the full theme)
      const themeData = {
        companyId,
        brandColor: company.brandColor,
        brandTextColor: company.brandTextColor || getContrastColor(company.brandColor),
        logoUrl: company.logoUrl,
        companyName: company.name,
        _cached: Date.now()
      };

      localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(themeData));
      
      // Apply theme immediately
      this.applyTheme(company);
      
      logger.info('[CompanyCache] Theme cached and applied', { 
        companyId, 
        brandColor: company.brandColor 
      });
    } catch (error) {
      logger.warn('[CompanyCache] Failed to cache theme', { 
        companyId, 
        error: error.message 
      });
    }
  }

  /**
   * Get and apply cached theme for the current user
   * @param {string} expectedCompanyId - The company ID we expect to see
   * @returns {object|null} Cached theme data or null
   */
  getCachedTheme(expectedCompanyId) {
    try {
      const cachedTheme = localStorage.getItem(THEME_CACHE_KEY);
      if (!cachedTheme) return null;

      const themeData = JSON.parse(cachedTheme);
      const cacheAge = Date.now() - (themeData._cached || 0);

      // Check if cache is expired or for a different company
      if (cacheAge > MAX_CACHE_AGE || themeData.companyId !== expectedCompanyId) {
        this.clearTheme();
        return null;
      }

      // Apply cached theme immediately
      this.applyTheme(themeData);

      logger.info('[CompanyCache] Applied cached theme', { 
        companyId: themeData.companyId,
        brandColor: themeData.brandColor,
        cacheAge: Math.round(cacheAge / 1000 / 60) // minutes
      });

      return themeData;
    } catch (error) {
      logger.warn('[CompanyCache] Failed to apply cached theme', { 
        error: error.message 
      });
      this.clearTheme();
      return null;
    }
  }

  /**
   * Apply full theme to document (all 15 CSS variables)
   * @param {object} company - Must have brandColor; optionally brandTextColor
   */
  applyTheme(company) {
    if (!company?.brandColor) {
      this.resetTheme();
      return;
    }

    const hsl = this.hexToHSL(company.brandColor);

    // Primary variables
    document.body.style.setProperty("--bulma-primary-h", `${hsl.h}deg`);
    document.body.style.setProperty("--bulma-primary-s", `${hsl.s}%`);
    document.body.style.setProperty("--bulma-primary-l", `${hsl.l}%`);
    document.body.style.setProperty(
      "--bulma-primary",
      `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, 1)`
    );

    // Contrast / invert
    const contrastColor =
      company.brandTextColor || getContrastColor(company.brandColor);
    document.body.style.setProperty("--bulma-primary-invert", contrastColor);

    // Info bar + secondary variables based on contrast
    if (contrastColor === "#231F20") {
      // Light brand color — dark contrast needed
      document.body.style.setProperty("--info-bar-bg", "#231F20");
      document.body.style.setProperty("--info-bar-color", company.brandColor);
      document.body.style.setProperty("--info-bar-title-color", "#FFFFFF");

      document.body.style.setProperty(
        "--company-secondary-bg",
        `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, .1)`
      );
      document.body.style.setProperty(
        "--company-secondary-bg-hover",
        `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, 0)`
      );
      document.body.style.setProperty(
        "--company-secondary-bg-active",
        `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, .2)`
      );
      document.body.style.setProperty(
        "--company-secondary-color",
        company.brandColor
      );
      document.body.style.setProperty(
        "--company-secondary-border",
        company.brandColor
      );
      document.body.style.setProperty(
        "--company-secondary-solid-bg",
        company.brandColor
      );
      document.body.style.setProperty(
        "--company-secondary-solid-color",
        "#231F20"
      );
    } else {
      // Dark brand color — white contrast needed
      document.body.style.setProperty("--info-bar-bg", company.brandColor);
      document.body.style.setProperty("--info-bar-color", "#FFFFFF");
      document.body.style.setProperty("--info-bar-title-color", "#FFFFFF");

      document.body.style.setProperty(
        "--company-secondary-bg",
        "hsla(0, 0%, 100%, .1)"
      );
      document.body.style.setProperty(
        "--company-secondary-bg-hover",
        "hsla(0, 0%, 100%, 0)"
      );
      document.body.style.setProperty(
        "--company-secondary-bg-active",
        "hsla(0, 0%, 100%, .2)"
      );
      document.body.style.setProperty("--company-secondary-color", "#FFFFFF");
      document.body.style.setProperty("--company-secondary-border", "#FFFFFF");
      document.body.style.setProperty("--company-secondary-solid-bg", "#FFFFFF");
      document.body.style.setProperty(
        "--company-secondary-solid-color",
        company.brandColor
      );
    }
  }

  /**
   * Reset theme to default (clears all 15 CSS variables)
   */
  resetTheme() {
    document.body.style.removeProperty("--bulma-primary-h");
    document.body.style.removeProperty("--bulma-primary-s");
    document.body.style.removeProperty("--bulma-primary-l");
    document.body.style.removeProperty("--bulma-primary");
    document.body.style.removeProperty("--bulma-primary-invert");

    document.body.style.removeProperty("--info-bar-bg");
    document.body.style.removeProperty("--info-bar-color");
    document.body.style.removeProperty("--info-bar-title-color");

    document.body.style.removeProperty("--company-secondary-bg");
    document.body.style.removeProperty("--company-secondary-bg-hover");
    document.body.style.removeProperty("--company-secondary-bg-active");
    document.body.style.removeProperty("--company-secondary-color");
    document.body.style.removeProperty("--company-secondary-border");
    document.body.style.removeProperty("--company-secondary-solid-bg");
    document.body.style.removeProperty("--company-secondary-solid-color");
  }

  /**
   * Clear cached theme
   */
  clearTheme() {
    try {
      localStorage.removeItem(THEME_CACHE_KEY);
      this.resetTheme();
      logger.debug('[CompanyCache] Theme cache cleared');
    } catch (error) {
      logger.warn('[CompanyCache] Failed to clear theme cache', { 
        error: error.message 
      });
    }
  }

  /**
   * Convert hex color to HSL
   * @param {string} hex 
   * @returns {object} HSL values
   */
  hexToHSL(hex) {
    // Convert hex to RGB first
    let r = 0, g = 0, b = 0;
    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex.substring(1, 3), 16);
      g = parseInt(hex.substring(3, 5), 16);
      b = parseInt(hex.substring(5, 7), 16);
    }

    // Convert RGB to HSL
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  }

  /**
   * Clear all company cache data
   */
  clearAll() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      this.clearTheme();
      logger.info('[CompanyCache] All cache data cleared');
    } catch (error) {
      logger.warn('[CompanyCache] Failed to clear all cache data', { 
        error: error.message 
      });
    }
  }

  /**
   * Get all cached companies (useful for logged-out scenarios)
   * @returns {Array} Array of cached company objects with their IDs
   */
  getAllCachedCompanies() {
    const cachedCompanies = [];
    
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          const companyId = key.replace(CACHE_PREFIX, '');
          const company = this.getCompany(companyId);
          if (company) {
            cachedCompanies.push({
              id: companyId,
              ...company
            });
          }
        }
      });
      
      logger.debug('[CompanyCache] Retrieved all cached companies', { 
        count: cachedCompanies.length 
      });
    } catch (error) {
      logger.warn('[CompanyCache] Failed to get all cached companies', { 
        error: error.message 
      });
    }

    return cachedCompanies;
  }

  /**
   * Extract company ID from URL path (useful for logged-out states)
   * @param {string} path - URL path like '/accounts/123/assets'
   * @returns {string|null} Company ID or null if not found
   */
  extractCompanyIdFromPath(path) {
    if (!path) return null;
    
    // Match pattern: /accounts/{companyId}/...
    const match = path.match(/^\/accounts\/([^/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Get company data from cache by URL path (useful for login page branding)
   * @param {string} path - URL path to extract company ID from
   * @returns {object|null} Cached company data or null
   */
  getCompanyFromPath(path) {
    const companyId = this.extractCompanyIdFromPath(path);
    return companyId ? this.getCompany(companyId) : null;
  }

  /**
   * Apply theme from path without requiring authentication
   * @param {string} path - URL path to extract company ID from
   * @returns {boolean} True if theme was applied, false otherwise
   */
  applyThemeFromPath(path) {
    const company = this.getCompanyFromPath(path);
    if (company) {
      this.applyTheme(company);
      logger.info('[CompanyCache] Applied theme from path', { 
        path, 
        companyId: this.extractCompanyIdFromPath(path),
        companyName: company.name 
      });
      return true;
    }
    return false;
  }

  /**
   * Get the most recently accessed company (useful for suggesting last company on login)
   * @returns {object|null} Most recently cached company or null
   */
  getMostRecentCompany() {
    let mostRecent = null;
    let latestTimestamp = 0;

    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          const cachedData = localStorage.getItem(key);
          if (cachedData) {
            try {
              const companyData = JSON.parse(cachedData);
              const timestamp = companyData._cached || 0;
              
              if (timestamp > latestTimestamp) {
                // Check if cache is still valid
                const cacheAge = Date.now() - timestamp;
                if (cacheAge <= MAX_CACHE_AGE) {
                  latestTimestamp = timestamp;
                  const { _cached, _cacheVersion, ...cleanData } = companyData;
                  mostRecent = {
                    id: key.replace(CACHE_PREFIX, ''),
                    ...cleanData
                  };
                }
              }
            } catch {
              // Skip invalid cache entries
            }
          }
        }
      });

      if (mostRecent) {
        logger.debug('[CompanyCache] Found most recent company', { 
          companyId: mostRecent.id,
          companyName: mostRecent.name,
          cacheAge: Math.round((Date.now() - latestTimestamp) / 1000 / 60) // minutes
        });
      }
    } catch (error) {
      logger.warn('[CompanyCache] Failed to get most recent company', { 
        error: error.message 
      });
    }

    return mostRecent;
  }

  /**
   * Check if company data is cached (without retrieving it)
   * @param {string} companyId 
   * @returns {boolean} True if company is cached and valid
   */
  hasValidCache(companyId) {
    if (!companyId) return false;

    try {
      const cachedData = localStorage.getItem(`${CACHE_PREFIX}${companyId}`);
      if (!cachedData) return false;

      const companyData = JSON.parse(cachedData);
      const cacheAge = Date.now() - (companyData._cached || 0);

      return cacheAge <= MAX_CACHE_AGE;
    } catch {
      return false;
    }
  }

  /**
   * Get cache statistics (useful for debugging)
   * @returns {object} Cache statistics
   */
  getCacheStats() {
    const stats = {
      totalCachedCompanies: 0,
      validCachedCompanies: 0,
      expiredCachedCompanies: 0,
      hasActiveTheme: false,
      oldestCacheAge: 0,
      newestCacheAge: 0
    };

    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      let oldestTimestamp = now;
      let newestTimestamp = 0;

      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          stats.totalCachedCompanies++;
          
          try {
            const cachedData = localStorage.getItem(key);
            const companyData = JSON.parse(cachedData);
            const timestamp = companyData._cached || 0;
            const cacheAge = now - timestamp;

            if (timestamp < oldestTimestamp) oldestTimestamp = timestamp;
            if (timestamp > newestTimestamp) newestTimestamp = timestamp;

            if (cacheAge <= MAX_CACHE_AGE) {
              stats.validCachedCompanies++;
            } else {
              stats.expiredCachedCompanies++;
            }
          } catch {
            stats.expiredCachedCompanies++;
          }
        }
      });

      // Check for active theme
      const themeCache = localStorage.getItem(THEME_CACHE_KEY);
      if (themeCache) {
        try {
          const themeData = JSON.parse(themeCache);
          const themeAge = now - (themeData._cached || 0);
          stats.hasActiveTheme = themeAge <= MAX_CACHE_AGE;
        } catch {
          stats.hasActiveTheme = false;
        }
      }

      stats.oldestCacheAge = oldestTimestamp < now ? Math.round((now - oldestTimestamp) / 1000 / 60) : 0;
      stats.newestCacheAge = newestTimestamp > 0 ? Math.round((now - newestTimestamp) / 1000 / 60) : 0;

    } catch (error) {
      logger.warn('[CompanyCache] Failed to get cache stats', { 
        error: error.message 
      });
    }

    return stats;
  }
}

export const companyCache = new CompanyCache(); 